'use client';

/**
 * @module
 * @category Component
 */

import { Status } from 'BomdisoftComponents/chips/status';
import { TabOverflowArrows, type TabOverflowLabels, useTabOverflow } from 'BomdisoftComponents/tab/overflow';
import 'BomdisoftComponents/tab/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { TabDetails } from 'BomdisoftComponents/types';
import { getLanguageFromLocale, navigationTabsT } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type KeyboardEvent, useId, useState } from 'react';

type HorizontalTabProps = {
  tabs: TabDetails[];
  defaultTab?: number;
  variant?: 'scrollable' | 'standard' | 'fullWidth';
  onTabChange?: (activeTab: TabDetails) => void;
  className?: string;
  listClassName?: string;
  tabClassName?: string;
  panelClassName?: string;
  /** Translated names for the scroll chevrons, shown when the row overflows. */
  scrollLabels?: TabOverflowLabels;
  /** Roots the test ids: `<testId>`, `<testId>-tab-<id>` and `<testId>-panel`. */
  testId?: string;
};

export const HorizontalTab = ({ tabs, defaultTab = 0, variant = 'scrollable', onTabChange, className, listClassName, tabClassName, panelClassName, scrollLabels, testId }: HorizontalTabProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const [activeTabIndex, setActiveTabIndex] = useState(defaultTab);
  const baseId = useId();
  const { frameProps, page, rowProps, scrollerClassName } = useTabOverflow({ selected: activeTabIndex });

  const handleClick = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);
    if (typeof onTabChange == 'function') onTabChange(tabs[tabIndex]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, number> = { ArrowLeft: activeTabIndex - 1, ArrowRight: activeTabIndex + 1, End: tabs.length - 1, Home: 0 };
    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const target = (next + tabs.length) % tabs.length;
    handleClick(target);
    const moved = event.currentTarget.children[target] as HTMLElement | undefined;
    moved?.focus();
    moved?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  if (!tabs || !Array.isArray(tabs) || tabs.length < 1) return null;

  const tabName = (tab: TabDetails) => (typeof tab.name === 'string' ? tab.name : tab.name[language]);

  return (
    <div className={joinClasses('bomdisoft-tabs', className)} data-testid={testId} data-variant={variant}>
      <div {...frameProps}>
        <div aria-label={navigationTabsT[language]} className={joinClasses('bomdisoft-tabs-list', scrollerClassName, listClassName)} onKeyDown={handleKeyDown} role='tablist' {...rowProps}>
          {tabs.map((tab, index) => {
            const name = tabName(tab);
            return (
              <button
                aria-controls={`${baseId}-panel`}
                aria-selected={index === activeTabIndex}
                className={joinClasses('bomdisoft-tabs-tab', tabClassName)}
                data-testid={testId ? `${testId}-tab-${tab.id}` : undefined}
                id={`${baseId}-tab-${index}`}
                key={tab.id}
                onClick={() => handleClick(index)}
                role='tab'
                tabIndex={index === activeTabIndex ? 0 : -1}
                type='button'
              >
                {tab.showBadge ? (
                  <span className='bomdisoft-tabs-badge-label'>
                    {name}
                    <span className='bomdisoft-tabs-badge-tag'>
                      <Status text={tab.count?.toString() || '0'} variant='info' />
                    </span>
                  </span>
                ) : (
                  name
                )}
              </button>
            );
          })}
        </div>
        <TabOverflowArrows labels={scrollLabels} onPage={page} />
      </div>
      <section aria-labelledby={`${baseId}-tab-${activeTabIndex}`} className={joinClasses('bomdisoft-tabs-panel', panelClassName)} data-testid={testId ? `${testId}-panel` : undefined} id={`${baseId}-panel`} role='tabpanel'>
        {tabs[activeTabIndex]?.component || ''}
      </section>
    </div>
  );
};
